import { useAtom } from 'jotai';
import { atomWithQuery, atomWithMutation } from 'jotai-tanstack-query';
import { brandService } from '@/services';
import { invalidateQueries, queryClient } from '@/lib/query-client';
import { buScopeKey, buScopeParams } from '@/lib/business-unit-scope';
import { isAuthenticatedAtom } from './use-auth';
import { helpdeskTicketFieldsQuery } from './helpdesk-ticket-fields-query';
import type { AutomationGuards, TicketFieldValue, UpdateAutomationThresholdDto, UpdateBrandSettingsDto } from '@/types';

// The brand is in every settings key as well as in the request. Two brands must never
// read each other's cached settings, and switching brands no longer clears the cache —
// a key without the brand would serve the previous brand's values.
const brandSettingsQueryAtom = atomWithQuery((get) => ({
  queryKey: ['brand', 'settings', buScopeKey(get)],
  queryFn: () => brandService.getSettings(buScopeParams(get)),
  staleTime: 5 * 60 * 1000,
  enabled: get(isAuthenticatedAtom)
}));

const updateBrandSettingsMutationAtom = atomWithMutation((get) => ({
  mutationKey: ['brand', 'settings', 'update'],
  mutationFn: (data: UpdateBrandSettingsDto) => brandService.updateSettings(data, buScopeParams(get)),
  onSuccess: () => {
    invalidateQueries(['brand', 'settings']);
  }
}));

const updateAutomationThresholdMutationAtom = atomWithMutation((get) => ({
  mutationKey: ['brand', 'automation-threshold', 'update'],
  mutationFn: (data: UpdateAutomationThresholdDto) =>
    brandService.updateAutomationThreshold(data, buScopeParams(get)),
  onSuccess: () => {
    invalidateQueries(['brand', 'settings']);
  }
}));

const setAutomationGuardsMutationAtom = atomWithMutation((get) => ({
  mutationKey: ['brand', 'automation-guards', 'set'],
  mutationFn: (guards: AutomationGuards) => brandService.setAutomationGuards(guards, buScopeParams(get)),
  onSuccess: () => {
    invalidateQueries(['brand', 'settings']);
  }
}));

export function useSetAutomationGuards() {
  const [mutation] = useAtom(setAutomationGuardsMutationAtom);
  return mutation;
}

export function useBrandSettings() {
  const [query] = useAtom(brandSettingsQueryAtom);
  return query;
}

export function useUpdateBrandSettings() {
  const [mutation] = useAtom(updateBrandSettingsMutationAtom);
  return mutation;
}

export function useUpdateAutomationThreshold() {
  const [mutation] = useAtom(updateAutomationThresholdMutationAtom);
  return mutation;
}

const blockedIntentsQueryAtom = atomWithQuery((get) => ({
  queryKey: ['brand', 'blocked-intents', buScopeKey(get)],
  queryFn: () => brandService.getBlockedIntents(buScopeParams(get)),
  staleTime: 5 * 60 * 1000,
  enabled: get(isAuthenticatedAtom)
}));

const setBlockedIntentsMutationAtom = atomWithMutation((get) => ({
  mutationKey: ['brand', 'blocked-intents', 'set'],
  mutationFn: (payload: { intentIds: string[]; autoCloseIntentIds: string[] }) =>
    brandService.setBlockedIntents(payload, buScopeParams(get)),
  // Returned, so the save stays pending until the new lists load: each save sends the whole list.
  onSuccess: () => invalidateQueries(['brand', 'blocked-intents'])
}));

export function useBlockedIntents() {
  const [query] = useAtom(blockedIntentsQueryAtom);
  return query;
}

export function useSetBlockedIntents() {
  const [mutation] = useAtom(setBlockedIntentsMutationAtom);
  return mutation;
}

// Account-wide, like the helpdesk they belong to: one value whichever brand is selected.
const spamTicketFieldsQueryAtom = atomWithQuery((get) => ({
  queryKey: ['brand', 'spam-ticket-fields'],
  queryFn: () => brandService.getSpamTicketFields(),
  staleTime: 5 * 60 * 1000,
  enabled: get(isAuthenticatedAtom)
}));

type SpamTicketFieldsUpdate = { intentId: string; ticketFields: TicketFieldValue[] };

/** The per-intent map with one intent's saved values applied; an empty list removes the intent. */
export const withSavedSpamTicketFields = (
  current: Record<string, TicketFieldValue[]> | undefined,
  intentId: string,
  saved: TicketFieldValue[]
): Record<string, TicketFieldValue[]> => {
  const next = { ...current };
  if (saved.length > 0) next[intentId] = saved;
  else delete next[intentId];
  return next;
};

const setSpamTicketFieldsMutationAtom = atomWithMutation(() => ({
  mutationKey: ['brand', 'spam-ticket-fields', 'set'],
  mutationFn: ({ intentId, ticketFields }: SpamTicketFieldsUpdate) => brandService.setSpamTicketFields(intentId, ticketFields),
  // Written into the cache straight away, so a second edit made before the refetch lands builds on
  // this one instead of the stale list.
  onSuccess: (saved: TicketFieldValue[], { intentId }: SpamTicketFieldsUpdate) => {
    queryClient.setQueryData<Record<string, TicketFieldValue[]>>(['brand', 'spam-ticket-fields'], (current) =>
      withSavedSpamTicketFields(current, intentId, saved)
    );
    invalidateQueries(['brand', 'spam-ticket-fields']);
  }
}));

// The helpdesk's fields are read only while some spam intent auto-closes.
const spamHelpdeskTicketFieldsQueryAtom = atomWithQuery((get) =>
  helpdeskTicketFieldsQuery(get, (get(blockedIntentsQueryAtom).data?.spamAutoCloseIntents.length ?? 0) > 0)
);

export function useSpamTicketFields() {
  const [query] = useAtom(spamTicketFieldsQueryAtom);
  return query;
}

export function useSetSpamTicketFields() {
  const [mutation] = useAtom(setSpamTicketFieldsMutationAtom);
  return mutation;
}

export function useSpamHelpdeskTicketFields() {
  const [query] = useAtom(spamHelpdeskTicketFieldsQueryAtom);
  return query;
}
